import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { CardComponent } from '../../../components/card/card.component';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { lastValueFrom } from 'rxjs';
import { UserService } from '../../../services/user.service';
import { ThemeService } from '../../../services/theme.service';
import { LogoImagesComponent } from '../../../components/logo-images/logo-images.component';
import { PasswordToggleComponent } from '../../../components/password-toggle/password-toggle.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    RouterModule,
    CardComponent,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    LogoImagesComponent,
    PasswordToggleComponent
  ]
})
export class LoginPage implements OnInit {
  loginForm!: FormGroup<{
    usernameOrEmail: FormControl<string | null>;
    password: FormControl<string | null>;
  }>;

  loginError: string | null = null;
  isLoading = false;

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private formBuilder: FormBuilder,
    private router: Router,
    private themeService: ThemeService
  ) {}

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      usernameOrEmail: ['', [Validators.required, this.emailOrUsernameValidator]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  async onLogin() {
    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.loginError = null;
    this.loginForm.disable();

    try {
      const response = await lastValueFrom(
        this.authService.login({
          username: this.loginForm.value.usernameOrEmail ?? '',
          password: this.loginForm.value.password ?? ''
        })
      );

      if (response.accessToken) {
        const me = await lastValueFrom(this.userService.fetchMe());

        if (me.employees && me.employees.length > 0) {
          this.themeService.updateBrandingColor(me.appStyle.idenitityColor);
          this.themeService.updateCtaColor(me.appStyle.primaryButtonColor);

          // Set up available companies if present in user data
          this.authService.setAvailableCompaniesFromUser(me.employees);

          await this.router.navigate(['/p/home']);
        } else {
          //no employees found, invalidate token and show error
          this.authService.logout(false);
          this.loginError = 'Er is geen actief profiel ingesteld. Neem contact op je opleider.';
        }
      }
    } catch (error) {
      this.loginError =
        'Er is iets misgegaan tijdens het inloggen. Controleer of je gebruikersnaam en wachtwoord correct zijn en of je je e-mailadres geverifieerd hebt.';
    } finally {
      this.isLoading = false;
      this.loginForm.enable();
    }
  }

  private emailOrUsernameValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }

    const isEmail = Validators.email(control) === null;
    const isUsername = value.length >= 4;

    if (isEmail || isUsername) {
      return null;
    }

    return { emailOrUsername: true };
  }
}
